// The mouse-aim director: a world-anchored "desired direction of flight".
//
// This is the contract War Thunder's instructor runs on (Gaijin's patent
// US 2013/0252208: "simulated aircraft control through desired direction of
// flight"): the mouse does not deflect a stick and does not hold an angle off
// the nose — it moves an aim direction that lives in the WORLD. Stop moving
// the mouse and the aim stays exactly where you put it while the plane rolls,
// pulls, and STOPS on it. A flick is a finite order — "fly THERE" — not a
// turn rate you have to keep feeding.
//
// We shipped the other thing first: the reticle was an angle off the nose
// (hold it deflected and the plane turns forever), patched with a bleed that
// eased the reticle home whenever the hand rested. That pairing is why the
// field reports read "the plane is hard to steer and tends to turn on its
// own": any pixel the bleed hadn't eaten yet kept the nose hauling — the
// mouse_feel_probe clocked a Sparrowhawk turning 210 MORE degrees after the
// hand stopped — while holding a captured aim cost 178 px/s of continuous
// mouse feed against the bleed, a treadmill no low-DPI mouse can walk (the
// same probe's fine-track scenario never settled at all: 18.6 deg RMS of
// standing error, aileron flailing at 0.88). World-anchoring dissolves both
// complaints at the source: a still hand is a standing order, and a turn is
// exactly as long as the drag that asked for it.
//
// The reticle you see is this aim direction projected through the same
// virtual chase camera the steering has always used — pinned rigidly to the
// predicted pose, so it is a pure function of state with no frame lag (the
// low-fps oscillator fix, kept). The projection runs every frame, so the
// reticle is glued to a spot in the world and slides across the screen as the
// plane converges under it — the nose visibly "catching up to the marker",
// which is the War Thunder picture drawn from our own camera.
//
// What the settled hand means (the instructor's half of the deal): after
// IDLE_AFTER_S of mouse quiet the aim's pitch eases to the horizon and, once
// the nose has converged, the wings level on autopilotLevel's proven gains —
// hands off the mouse, straight and level, current heading. That is the same
// promise the old bleed made to lost new pilots, but it no longer taxes the
// fighter: the ease touches only the VERTICAL, so a mid-drag turn is never
// unwound, and the moment the hand moves the instructor lets go entirely.
//
// Everything here is plain FDM-kit math (no THREE), so the headless
// mouse_feel_probe drives this exact code — tuning measured there is tuning
// shipped here.

import * as FDM from "./fdm.js";

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

// The steering circle (screen NDC radius). The aim can never leave it, which
// doubles as the WT screen-bounded marker: maneuver away and the circle's rim
// drags the aim with the plane instead of losing it behind you.
export const AIM_MAX_R = 0.55;
// NDC per pixel of mouse travel at sensitivity 1.0 — the shipped feel, now
// scaled by the Settings slider (va-mouse-sens) for the low-DPI hands.
export const SENS_BASE = 0.0022;
// Hand quiet this long = "settled"; the instructor's holds ease in over the
// ramp rather than snapping the moment the hand pauses between corrections.
const IDLE_AFTER_S = 0.4;
const IDLE_RAMP_S = 0.6;
// The settled-hand rescue: an aim left STEEPLY off the horizon eases back to
// the edge of a dead band, so "flicked the aim into the dirt and walked away"
// becomes a shallow drift instead of a lawn dart. The dead band is the
// important half: inside ±5 degrees the aim is SOVEREIGN — a gun solution
// parked 1.5 degrees above the horizon must stay parked. The first cut eased
// everything to level and the fine-track probe caught it re-growing a
// settled 0.3-degree aim error to 0.8 and holding it there — the old bleed's
// sin in miniature, a director fighting its own pilot.
const LEVEL_EASE = 0.5;
const LEVEL_BAND = 0.087; // ±5 deg of aim pitch the ease never touches
// Wings-level capture for the settled+converged state: autopilotLevel's
// delay-tolerant gains, verbatim — they stay settled past ~130 ms of loop
// delay across the whole fleet, and this loop rides the same laggy path.
const LEVEL_ROLL_P = 2.0;
const LEVEL_ROLL_D = 0.5;

export class AimDirector {
  constructor() {
    this.aim = FDM.v3(1, 0, 0); // desired direction of flight, world frame
    this.cursor = { x: 0, y: 0 }; // its projection through the virtual camera (NDC)
    this.hasAim = false; // false until the first flying frame parks it level
    this.idleFor = 0; // seconds since the hand last fed the aim
    this.idleW = 0; // 0..1 settled weight the wings-level capture rides
  }

  /** The WALKED-AWAY weight for main.js's path hold: 0 until the hand has
   *  been still for 2.5 s, full by 4 s. Deliberately much slower than idleW —
   *  a gunnery pause between corrections is a second or two of stillness,
   *  and the fine-track probe caught the hold engaging inside it, shoving
   *  the nose alpha-above a parked gun solution (settle time 4.5 s instead
   *  of 1.6 s). Leveling the flight path is for pilots who actually left. */
  holdW() {
    const c = (this.idleFor - 2.5) / 1.5;
    return c < 0 ? 0 : c > 1 ? 1 : c;
  }

  /** The virtual chase camera's basis for a predicted pose — eye 19 back +
   *  8.5 up, look 40 ahead, up 35% toward plane-up, exactly the spring camera
   *  the render settles to (keep in lockstep with the flying chase camera in
   *  main.js). Rigid to the pose: no frame lag at any fps. */
  basis(state, fovDeg, aspect) {
    const fwd = FDM.qRotate(state.att, FDM.v3(1, 0, 0));
    const planeUp = FDM.qRotate(state.att, FDM.v3(0, 1, 0));
    const f = FDM.vNorm(FDM.vSub(FDM.vScale(fwd, 59), FDM.v3(0, 8.5, 0)));
    const upMix = FDM.vNorm(FDM.v3(planeUp.x * 0.35, 1 + (planeUp.y - 1) * 0.35, planeUp.z * 0.35));
    let right = FDM.vCross(f, upMix);
    const rl = FDM.vLen(right);
    if (rl < 1e-4) return null; // looking straight along the up mix — caller aims dead ahead
    right = FDM.vScale(right, 1 / rl);
    const up = FDM.vCross(right, f);
    const tanY = Math.tan((fovDeg * Math.PI) / 360);
    return { f, right, up, tanX: tanY * aspect, tanY };
  }

  rayFromCursor(b, cx, cy) {
    return FDM.vNorm(
      FDM.vAdd(b.f, FDM.vAdd(FDM.vScale(b.right, cx * b.tanX), FDM.vScale(b.up, cy * b.tanY))),
    );
  }

  /** Park the aim straight ahead and level — every spawn starts trimmed on
   *  the horizon, whatever the menu mouse did last. */
  park(state) {
    const fwd = FDM.qRotate(state.att, FDM.v3(1, 0, 0));
    const flat = Math.hypot(fwd.x, fwd.z);
    this.aim = flat > 1e-6 ? FDM.v3(fwd.x / flat, 0, fwd.z / flat) : FDM.v3(1, 0, 0);
    this.hasAim = true;
    this.idleFor = 0;
    this.idleW = 0;
  }

  drop() {
    this.hasAim = false;
    this.idleW = 0;
  }

  /**
   * Once per frame while flying. dxPx/dyPx are this frame's pointer-lock
   * mouse pixels (y up, elevator convention already applied); sens is the
   * player's slider. In pointer lock the aim is world-anchored; without it
   * (the first pre-click seconds) the absolute cursor steers the old way —
   * an angle off the nose — because an OS cursor is a screen position and
   * has no deltas to anchor with. handBusy holds the settle clock while the
   * hand is provably occupied elsewhere (freelook orbit, keyboard flying).
   */
  frame(state, { dxPx = 0, dyPx = 0, sens = 1, dt, fovDeg, aspect, locked, absX = 0, absY = 0, handBusy = false }) {
    const b = this.basis(state, fovDeg, aspect);
    if (!locked) {
      // Absolute mode: the visible OS cursor IS the reticle. Freelook freezes
      // the last world aim (the camera is orbiting; the plane must not chase
      // the swing) — otherwise re-derive from the cursor every frame.
      this.cursor.x = absX;
      this.cursor.y = absY;
      if (b && !(handBusy && this.hasAim)) this.aim = this.rayFromCursor(b, absX, absY);
      this.hasAim = true;
      this.idleFor = 0;
      this.idleW = 0;
      return;
    }
    if (!this.hasAim || !b) {
      if (b) this.park(state);
      return;
    }

    // World -> screen: where the standing order sits this frame. If the plane
    // has hauled the aim outside the view cone (or behind it), the rim drags
    // it along — the previous cursor holds and the aim re-derives from it.
    const a = FDM.vDot(this.aim, b.f);
    if (a > 0.15) {
      this.cursor.x = FDM.vDot(this.aim, b.right) / (a * b.tanX);
      this.cursor.y = FDM.vDot(this.aim, b.up) / (a * b.tanY);
    }

    // The hand's say: pixels scale by the slider, then the rim clamps.
    const moved = Math.abs(dxPx) + Math.abs(dyPx) > 0.5;
    this.cursor.x += dxPx * SENS_BASE * sens;
    this.cursor.y += dyPx * SENS_BASE * sens;
    const r = Math.hypot(this.cursor.x, this.cursor.y);
    if (r > AIM_MAX_R) {
      this.cursor.x *= AIM_MAX_R / r;
      this.cursor.y *= AIM_MAX_R / r;
    }
    this.aim = this.rayFromCursor(b, this.cursor.x, this.cursor.y);

    // The settle clock, and the steep-departure rescue it earns. Vertical
    // only, and only OUTSIDE the dead band: a settled hand means "don't let
    // me dive into the dirt", never "stop my turn" or "take my gun solution"
    // — heading and any near-level aim are the pilot's, full stop.
    if (moved || handBusy) this.idleFor = 0;
    else this.idleFor += dt;
    this.idleW = clamp((this.idleFor - IDLE_AFTER_S) / IDLE_RAMP_S, 0, 1);
    if (this.idleW > 0) {
      const flat = Math.hypot(this.aim.x, this.aim.z);
      const pitchA = Math.asin(clamp(this.aim.y, -1, 1));
      const over = Math.abs(pitchA) - LEVEL_BAND;
      if (flat > 0.1 && over > 0) {
        // Ease the excess over the band toward zero, heading untouched.
        const eased = Math.sign(pitchA) * (LEVEL_BAND + over * Math.exp(-LEVEL_EASE * this.idleW * dt));
        const c = Math.cos(eased) / flat;
        this.aim = FDM.v3(this.aim.x * c, Math.sin(eased), this.aim.z * c);
      }
    }
  }

  /**
   * The steering solve, run at every input tick against the LIVE predicted
   * state (the aim is world-fixed, so a long frame never staircases the
   * command — the freelook "toward" mode proved this shape). On top of the
   * pursuit: once the hand is settled AND the nose has converged, the wings
   * capture level — WT's autoBankLevel, the roll half of "hands off,
   * straight and level" (the pitch half is main.js's path hold).
   */
  steer(state, limits) {
    const a = FDM.steerTowardAssist(state, this.aim, limits);
    if (this.idleW > 0) {
      const db = FDM.qRotate(FDM.qConj(state.att), this.aim);
      // The engage band, swept in the fine-track probe: at 0.04 it
      // abandons the heavy fighters, whose fine endgame needs flat wings
      // for the rudder to finish (Ironclad never settles, RMS 1.14); 0.06
      // settles the fleet (Ironclad 0.33). It used to arm-wrestle the jet
      // carve's shallow closing bank here — the compromise that left every
      // jet a standing ~1.3-degree miss — but the carve now stands its own
      // wings down inside fdm.js's carveW gate, so near the nose the carve
      // and this leveller want the same thing: level, rudder finishing.
      const alignW = clamp(1 - Math.hypot(db.y, db.z) / 0.06, 0, 1);
      const w = this.idleW * alignW;
      if (w > 0) {
        const up = FDM.qRotate(state.att, FDM.v3(0, 1, 0));
        const right = FDM.qRotate(state.att, FDM.v3(0, 0, 1));
        const bank = Math.atan2(-right.y, up.y);
        const level = clamp(-bank * LEVEL_ROLL_P - state.omega.x * LEVEL_ROLL_D, -1, 1);
        a.roll = a.roll * (1 - w) + level * w;
      }
    }
    return a;
  }
}
